/**
 * StockVideoModal
 *
 * Modal to preview and download stock videos with plan-aware CTAs and
 * Firebase URL handling.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Heart, Play, Download, Calendar, Tag, UserPlus } from 'lucide-react';
import { formatDate } from '../../../utils/dates/dateUtils';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../ui/notifications/toast-notification.jsx';
import './TemplateModal.css'; // Use the same CSS file as TemplateModal

// Function to convert Firebase Storage gs:// URLs to HTTP URLs or return local URLs as-is
const convertFirebaseStorageUrl = (url) => {
  if (!url) return url;
  
  // Return local URLs as-is (they start with /)
  if (url.startsWith('/')) {
    return url;
  }
  
  // Handle Firebase Storage gs:// URLs
  if (url.startsWith('gs://')) {
    // Extract bucket and path from gs://bucket/path
    const match = url.match(/^gs:\/\/([^\/]+)\/(.+)$/);
    if (!match) {
      return url; // Return as-is if format doesn't match
    }
    
    const [, bucket, path] = match;
    // Convert to Firebase Storage HTTP URL
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
  }
  
  // Return other URLs as-is
  return url;
};

const StockVideoModal = ({ 
  stockVideo, 
  isOpen, 
  onClose, 
  onNext, 
  onPrevious, 
  hasNavigation = false,
  isSaved = false,
  onSaveStockVideo,
  isLoggedIn = false,
  userPlan = 'basic'
}) => {
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [duration, setDuration] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };
  
  // Determine if we're on the landing page
  const isOnLandingPage = location.pathname === '/' || location.pathname === '/demo-templates';

  // Auto-play video when modal opens (muted)
  useEffect(() => {
    if (isOpen && videoRef.current && stockVideo?.videoUrl) {
      setVideoLoading(true); // Reset loading state when video changes
      videoRef.current.muted = true;
      videoRef.current.currentTime = 0;
      setVideoError(false);
    }
  }, [isOpen, stockVideo]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasNavigation) {
            onPrevious();
          }
          break;
        case 'ArrowRight':
          if (hasNavigation) {
            onNext();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrevious, hasNavigation]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Video event handlers for duration and loading
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setVideoLoading(false); // Video is loaded
    };
    const handleError = () => {
      setVideoError(true);
      setVideoLoading(false); // Stop loading on error
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
    };
  }, [stockVideo?.id]);

  const handleDownload = async () => {
    if (!stockVideo?.videoUrl || isDownloading) return;

    setIsDownloading(true);

    try {
      // Track stock video download
      if (stockVideo?.id) {
        const { downloadStockVideo } = await import('../../../api/media/stockVideosApi');
        downloadStockVideo(stockVideo.id).catch(err => {
          // Silently log error (non-critical operation)
          console.error('Failed to record stock video download:', err);
        });
      }

      const httpUrl = convertFirebaseStorageUrl(stockVideo.videoUrl);
      const response = await fetch(httpUrl);
      if (!response.ok) throw new Error('Failed to fetch video');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${stockVideo.title || 'stock-video'}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      addToast('Video downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      addToast('Failed to download video. Please try again.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle save/unsave stock video
  const handleSaveClick = () => {
    if (onSaveStockVideo) {
      onSaveStockVideo(stockVideo.id, !isSaved);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !stockVideo) return null;

  // Format dates - handle different date formats from backend
  const formatStockVideoDate = (dateField) => {
    if (!dateField) return 'Unknown';
    
    try {
      // Handle Firestore timestamp format with underscores
      if (dateField && typeof dateField === 'object' && dateField._seconds) {
        return formatDate(new Date(dateField._seconds * 1000));
      }
      // Handle Firestore timestamp format without underscores
      if (dateField && typeof dateField === 'object' && dateField.seconds) {
        return formatDate(new Date(dateField.seconds * 1000));
      }
      // Handle string dates
      if (typeof dateField === 'string') {
        return formatDate(new Date(dateField));
      }
      // Handle regular Date objects
      if (dateField instanceof Date) {
        return formatDate(dateField);
      }
      // Handle epoch timestamps
      if (typeof dateField === 'number') {
        return formatDate(new Date(dateField));
      }
      return 'Unknown';
    } catch (error) {
      console.log('Date formatting error:', error);
      return 'Unknown';
    }
  };

  const createdDate = formatStockVideoDate(stockVideo.createdAt || stockVideo.createdDate);

  // Determine button text and action based on user login status and page location
  const getButtonText = () => {
    if (videoLoading) return 'Loading Video...';
    if (isLoggedIn && !isOnLandingPage) return 'Download video';
    return 'Sign up to download video';
  };

  const getButtonIcon = () => {
    if (isLoggedIn && !isOnLandingPage) return <Download className="w-4 h-4" />;
    return <UserPlus className="w-4 h-4" />;
  };

  const getButtonAction = () => {
    if (isLoggedIn && !isOnLandingPage) return handleDownload;
    return null; // Will use Link navigation for sign up
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="animate-in fade-in zoom-in-95 duration-200 ease-out w-[95%] md:w-full mx-auto"
        style={{ animationFillMode: 'forwards' }}
      >
        <div 
          ref={modalRef}
          className="relative bg-white dark:bg-gray-900 rounded-xl md:rounded-2xl shadow-xl w-full max-w-7xl max-h-[85vh] md:max-h-[95vh] overflow-hidden mx-auto my-1 md:my-4 flex flex-col"
        >
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-2 right-2 md:top-4 md:right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors z-20 shadow-md"
            aria-label="Close"
          >
            <X className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
          </button>

          {/* Main Content Area (Handles Desktop Row / Mobile Column Layout internally) */}
          <div className="flex flex-col lg:flex-row flex-1 min-h-0">
            {/* Left side - Video Player - NOT scrollable, taller on mobile */}
            <div className={`lg:w-1/2 flex-shrink-0 flex items-center justify-center p-6 md:p-8 lg:p-12 relative`}>
              {/* Save/Unsave heart icon overlay - only show if user is logged in */}
              {isLoggedIn && onSaveStockVideo && (
                <button
                  onClick={handleSaveClick}
                  className={`absolute top-3 right-3 md:top-4 md:right-4 p-1.5 md:p-2 rounded-full ${
                    isSaved 
                      ? 'bg-gradient-to-r from-purple-500 to-purple-600 shadow-md' 
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700'
                  } transition-colors z-10`}
                  aria-label={isSaved ? "Unsave video" : "Save video"}
                >
                  <Heart 
                    className={`w-4 h-4 md:w-5 md:h-5 ${isSaved ? 'text-white fill-white' : 'text-gray-700 dark:text-gray-300'}`} 
                  />
                </button>
              )}
              
              {/* Video - clean and direct with Safari fixes */}
              {stockVideo.videoUrl && !videoError ? (
                <div 
                  className="relative flex items-center justify-center"
                  style={{
                    /* Safari-specific container fixes */
                    width: '100%',
                    height: 'auto',
                    maxHeight: '35vh',
                    /* Force hardware acceleration */
                    WebkitTransform: 'translateZ(0)',
                    transform: 'translateZ(0)',
                    /* Safari flexbox fallbacks */
                    display: 'flex',
                    WebkitBoxAlign: 'center',
                    WebkitAlignItems: 'center',
                    alignItems: 'center',
                    WebkitBoxPack: 'center',
                    WebkitJustifyContent: 'center',
                    justifyContent: 'center'
                  }}
                  data-media-query-sm="max-height: 45vh"
                  data-media-query-md="max-height: 50vh"
                  data-media-query-lg="max-height: 65vh"
                >
                  {/* Video Skeleton - maintains 16:9 aspect ratio (960 × 544) */}
                  {videoLoading && (
                    <div 
                      className="bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                      style={{
                        width: '100%',
                        aspectRatio: '16/9',
                        maxWidth: '100%',
                        maxHeight: '35vh'
                      }}
                    />
                  )}
                  
                  {/* Video element - completely hidden during loading */}
                  <video 
                    ref={videoRef}
                    src={convertFirebaseStorageUrl(stockVideo.videoUrl)}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="rounded-lg shadow-lg"
                    style={{
                      /* Safari-specific video styling */
                      objectFit: 'contain',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '35vh',
                      aspectRatio: '16/9',
                      display: videoLoading ? 'none' : 'block',
                      /* Safari video centering and hardware acceleration */
                      WebkitTransform: 'translateZ(0)',
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      /* Safari video containment */
                      WebkitTransformStyle: 'preserve-3d',
                      transformStyle: 'preserve-3d'
                    }}
                    onError={() => setVideoError(true)}
                    onLoadedData={() => setVideoLoading(false)}
                    onCanPlay={() => setVideoLoading(false)}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div 
                  className="w-full bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center"
                  style={{
                    aspectRatio: '16/9',
                    maxHeight: '35vh'
                  }}
                >
                  <div className="text-center">
                    <Play className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Video unavailable</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right side - Video details - scrollable section */}
            <div className="lg:w-1/2 flex-1 min-h-0 overflow-auto border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700">
              <div className="p-4 md:p-4 lg:p-10 h-full flex flex-col">
                <div className="flex flex-col flex-grow">
                  <div className="space-y-4 md:space-y-6 flex-grow text-sm md:text-base mb-4 md:mb-6">
                    {/* Info Items */}
                    <div className="space-y-4 md:space-y-6">
                      {/* Title */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                        <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Title:</div>
                        <div className="flex-1 text-gray-800 dark:text-white break-words font-medium">
                          {videoLoading ? (
                            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4"></div>
                          ) : (
                            stockVideo.title
                          )}
                        </div>
                      </div>

                      {/* Added Date */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                        <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Added:</div>
                        <div className="flex-1 flex items-center text-gray-800 dark:text-white">
                          <Calendar className="h-3 w-3 md:h-4 md:w-4 text-gray-400 dark:text-gray-300 mr-1.5 md:mr-2.5" />
                          {videoLoading ? (
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
                          ) : (
                            <span>{createdDate}</span>
                          )}
                        </div>
                      </div>

                      {/* Video Duration */}
                      {(duration > 0 || videoLoading) && (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                          <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Duration:</div>
                          <div className="flex-1 text-gray-800 dark:text-white">
                            {videoLoading ? (
                              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16"></div>
                            ) : (
                              formatTime(duration)
                            )}
                          </div>
                        </div>
                      )}


                    </div>
                  </div>
                  
                  {/* Footer buttons - Sign up and Close */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 md:pt-6 mt-auto flex-shrink-0">
                    <div className="flex flex-col space-y-3 pb-5 sm:pb-4 md:pb-0">
                      {/* Download/Sign up button */}
                      {isLoggedIn && !isOnLandingPage ? (
                        <button
                          onClick={handleDownload}
                          disabled={videoLoading || isDownloading}
                          className={`w-full py-2.5 md:py-3 px-3 md:px-4 ${
                            videoLoading || isDownloading
                              ? 'bg-gray-400 cursor-not-allowed pointer-events-none' 
                              : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                          } text-white rounded-md text-sm md:text-base font-medium transition-all duration-200 flex items-center justify-center gap-2`}
                          style={{
                            boxShadow: videoLoading || isDownloading ? "none" : "0 2px 6px rgba(168, 85, 247, 0.3)"
                          }}
                        >
                          {getButtonIcon()}
                          {isDownloading ? 'Downloading...' : getButtonText()}
                        </button>
                      ) : (
                        <Link
                          to="/signup"
                          className={`w-full py-2.5 md:py-3 px-3 md:px-4 ${
                            videoLoading 
                              ? 'bg-gray-400 cursor-not-allowed pointer-events-none' 
                              : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                          } text-white rounded-md text-sm md:text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 no-underline`}
                          style={{
                            boxShadow: videoLoading ? "none" : "0 2px 6px rgba(168, 85, 247, 0.3)"
                          }}
                        >
                          {getButtonIcon()}
                          {getButtonText()}
                        </Link>
                      )}
                      
                      {/* Close button - only visible on mobile */}
                      <button
                        onClick={onClose}
                        className="w-full py-2.5 px-3 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors lg:hidden mb-2 sm:mb-3 md:mb-4"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockVideoModal; 